import React, { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Flex, Heading, Spinner, Text } from '@chakra-ui/react';
import { useDragAndDrop } from '@codametrix/ui-components';
import { dragManager } from '@codametrix/ui-common';
import { useDragAutoScroll } from './use-drag-auto-scroll';
import { QueueCard } from '../../components/queue-card/QueueCard';
import { formatLoggedAtParts } from '../../project_starburst/slots/formatLoggedAt';
import {
  fetchQueues,
  savePriorityOrder,
  reorderQueues,
  reorderPinnedQueues,
  togglePin,
  revertOrder,
  QueueItem
} from '../../actions/queue-priority-order.action';
import { openModal } from '../../actions/manage-coders.action';
import { selectQueuePriorityOrder } from '../../reducers/queue-priority-order.reducer';
import { ManageCodersModalContainer } from '../manage-coders/ManageCodersModalContainer';
import { getUserRolesFromActiveContext } from '../../core/cqa/CQASubmitButton';
import { QueuePriorityHeaderActionsContext } from './queue-priority-header-actions-context';
import './QueuePriorityOrderView.scss';

const CODING_MANAGER_ROLE = 'coding_manager';

// The zone the nightly reset is scheduled in — QUEUE_METRICS_RECALC_TZ in every values file.
// It is a property of the schedule, not of the viewer, so it is not a per-tenant setting.
const RESET_TIME_ZONE = 'America/New_York';

interface DragPreview {
  fromIndex: number;
  toIndex: number;
  /** Index in the currentOrder-based DOM list where the placeholder is inserted. */
  domInsertIndex: number;
  /** Maps queue id → 1-based priority number in the preview order. */
  priorityMap: Map<string, number>;
}

interface DraggableQueueCardProps {
  queue: QueueItem;
  index: number;
  priority: number;
  item: CMxCommonApp.DraggableSectionType;
  allItems: CMxCommonApp.DraggableSectionType[];
  dm: CMxCommonApp.DragManager;
  onDropHandler: (action: CMxCommonApp.DroppedAction<any>) => void;
  onDragOverCallback?: (action: CMxCommonApp.DroppedAction<any>) => void;
  onDragEndCallback?: () => void;
  onManageCoders?: () => void;
  isPinned?: boolean;
  onTogglePin?: () => void;
  canTogglePin?: boolean;
}

interface PlaceholderCardProps {
  onDrop: () => void;
}

const canDropOnto = () => true;

const PlaceholderCard: React.FC<PlaceholderCardProps> = ({ onDrop }) => (
  <div
    className="cmx-queue-priority-order-view__placeholder"
    onDragOver={ev => ev.preventDefault()}
    onDrop={ev => { ev.preventDefault(); ev.stopPropagation(); onDrop(); }}
    data-testid="drag-placeholder"
  />
);

const DraggableQueueCard: React.FC<DraggableQueueCardProps> = ({
  queue,
  index,
  priority,
  item,
  allItems,
  dm,
  onDropHandler,
  onDragOverCallback,
  onDragEndCallback,
  onManageCoders,
  isPinned,
  onTogglePin,
  canTogglePin
}) => {
  const itemRef = React.useRef<HTMLDivElement>(null);

  const {
    isBeingDragged,
    dragHover,
    handleDragStart,
    onDragEnd,
    onDragLeave,
    onDragOver,
    onDrop
  } = useDragAndDrop({
    initialState: { isBeingDragged: false, dragHover: false, dragPosition: null },
    dragManager: dm,
    onDropHandler,
    onDragOverHandler: onDragOverCallback,
    onDragEndHandler: onDragEndCallback,
    canDropOnto,
    itemRef,
    item,
    allItems,
    dataKey: 'data',
    hitboxThreshold: 1 / 3
  });

  const className = [
    'cmx-queue-priority-order-view__item',
    dragHover ? 'cmx-queue-priority-order-view__item--drag-over' : '',
    isBeingDragged ? 'cmx-queue-priority-order-view__item--dragging' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={itemRef}
      className={className}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onDragLeave={onDragLeave}
      data-testid={`queue-item-${index}`}
    >
      <QueueCard
        priority={priority}
        queueName={queue.queueName}
        description={queue.description}
        status={queue.status}
        totalInQueue={queue.totalInQueue}
        criticalAged={queue.criticalAged}
        coderCount={queue.coderCount}
        onDragStart={handleDragStart}
        isDragging={isBeingDragged}
        isPinned={isPinned}
        onTogglePin={onTogglePin}
        canTogglePin={canTogglePin}
        onManageCoders={onManageCoders}
      />
    </div>
  );
};

/**
 * Same drag-preview/reorder logic as the original single-list view, parameterized so it
 * can drive both the pinned section and the main priority list independently (separate
 * dragManager keys keep the two sections from being drop targets for each other).
 */
const useDragSection = (
  currentOrder: string[],
  dmKey: string,
  onReorder: (fromIndex: number, toIndex: number) => void
) => {
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);

  const dm = React.useMemo(() => dragManager.load(dmKey), [dmKey]);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const isDragActive = React.useCallback(() => Boolean(dm.get()), [dm]);

  useDragAutoScroll(rootRef, isDragActive);

  const clearPreview = React.useCallback(() => {
    setDragPreview(null);
  }, []);

  const handleDragOverPreview = React.useCallback(
    ({ dropCandidate, dropTarget, position }: CMxCommonApp.DroppedAction<any>) => {
      const fromIndex = currentOrder.indexOf(dropCandidate.id);
      const targetIndex = currentOrder.indexOf(dropTarget.id);
      const toIndex =
        position === 'before'
          ? fromIndex < targetIndex ? targetIndex - 1 : targetIndex
          : fromIndex < targetIndex ? targetIndex : targetIndex + 1;

      if (fromIndex === toIndex) {
        setDragPreview(null);
        return;
      }

      const newOrder = [...currentOrder];
      const [moved] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, moved);

      // domInsertIndex: where the placeholder appears in the fixed DOM order.
      // Dragging down → placeholder goes after the card now sitting at toIndex.
      // Dragging up   → placeholder goes before the card now sitting at toIndex.
      const domInsertIndex = fromIndex < toIndex ? toIndex + 1 : toIndex;

      // Auto-scroll makes dragover continuous, so an unchanged preview must not
      // re-render the whole list on every frame.
      setDragPreview(previous =>
        previous &&
        previous.fromIndex === fromIndex &&
        previous.toIndex === toIndex &&
        previous.domInsertIndex === domInsertIndex
          ? previous
          : {
              fromIndex,
              toIndex,
              domInsertIndex,
              priorityMap: new Map(newOrder.map((id, i) => [id, i + 1]))
            }
      );
    },
    [currentOrder]
  );

  const handlePlaceholderDrop = React.useCallback(() => {
    if (!dragPreview || dragPreview.fromIndex === dragPreview.toIndex) return;
    const { fromIndex, toIndex } = dragPreview;
    setDragPreview(null);
    onReorder(fromIndex, toIndex);
  }, [dragPreview, onReorder]);

  const handleDrop = React.useCallback(
    ({ dropCandidate, dropTarget, position }: CMxCommonApp.DroppedAction<any>) => {
      setDragPreview(null);
      const fromIndex = currentOrder.indexOf(dropCandidate.id);
      const targetIndex = currentOrder.indexOf(dropTarget.id);
      const toIndex =
        position === 'before'
          ? fromIndex < targetIndex ? targetIndex - 1 : targetIndex
          : fromIndex < targetIndex ? targetIndex : targetIndex + 1;
      if (fromIndex !== toIndex) {
        onReorder(fromIndex, toIndex);
      }
    },
    [currentOrder, onReorder]
  );

  return { dm, rootRef, dragPreview, clearPreview, handleDragOverPreview, handlePlaceholderDrop, handleDrop };
};

export const QueuePriorityOrderView: React.FC = () => {
  const dispatch = useDispatch();
  const {
    queues,
    currentOrder,
    originalOrder,
    currentPinnedOrder = [],
    originalPinnedOrder = [],
    hasUserReordered = false,
    isLoading,
    isSaving,
    error,
    saveError,
    lastAutoResetAt
  } = useSelector(selectQueuePriorityOrder);
  const service = useSelector(
    (state: any) => state.ui?.serviceLine?.name as string | undefined
  );
  const userContext = useSelector((state: any) => state.ui?.context);
  const headerActionsNode = useContext(QueuePriorityHeaderActionsContext);

  // Rendered in the zone the reset actually fires in, not the viewer's. The schedule is
  // global (QUEUE_METRICS_RECALC_TZ is one value per deployment, America/New_York in every
  // chart), so a browser-zone render shows a Pacific manager the previous DATE for the same
  // reset. An unparseable value renders nothing at all — "Invalid Date" on a manager's
  // screen is worse than an absent line.
  const lastAutoResetInstant = lastAutoResetAt ? new Date(lastAutoResetAt) : null;
  const lastAutoResetParts =
    lastAutoResetInstant && !Number.isNaN(lastAutoResetInstant.getTime())
      ? formatLoggedAtParts(lastAutoResetInstant, RESET_TIME_ZONE)
      : null;
  const lastAutoResetLabel = lastAutoResetParts
    ? `Priority Order Last Auto-Reset: ${lastAutoResetParts.date} ${lastAutoResetParts.time}`
    : null;

  const isCodingManager = useMemo(
    () => getUserRolesFromActiveContext(userContext).includes(CODING_MANAGER_ROLE),
    [userContext]
  );

  const hasChanges =
    currentOrder.join(',') !== originalOrder.join(',') ||
    currentPinnedOrder.join(',') !== originalPinnedOrder.join(',');

  useEffect(() => {
    if (service) {
      dispatch(fetchQueues({ service }) as any);
    }
  }, [dispatch, service]);

  const queueMap = React.useMemo(
    () => new Map(queues.map(q => [q.id, q])),
    [queues]
  );

  const orderedQueues = currentOrder
    .map(id => queueMap.get(id))
    .filter(Boolean) as QueueItem[];

  const pinnedQueues = currentPinnedOrder
    .map(id => queueMap.get(id))
    .filter(Boolean) as QueueItem[];

  const handleReorder = React.useCallback(
    (fromIndex: number, toIndex: number) => {
      dispatch(reorderQueues({ fromIndex, toIndex }) as any);
    },
    [dispatch]
  );

  const handleReorderPinned = React.useCallback(
    (fromIndex: number, toIndex: number) => {
      dispatch(reorderPinnedQueues({ fromIndex, toIndex }) as any);
    },
    [dispatch]
  );

  const mainSection = useDragSection(currentOrder, 'queue-priority-order', handleReorder);
  const pinnedSection = useDragSection(
    currentPinnedOrder,
    'queue-priority-order-pinned',
    handleReorderPinned
  );

  const handleSave = React.useCallback(() => {
    if (service) {
      (
        dispatch(
          savePriorityOrder({
            service,
            orderedIds: currentOrder,
            pinnedIds: currentPinnedOrder,
            hasUserReordered
          }) as any
        ) as Promise<{ type: string }>
      ).then(result => {
        if (result?.type === savePriorityOrder.fulfilled.type) {
          dispatch(fetchQueues({ service }) as any);
        }
      });
    }
  }, [currentOrder, currentPinnedOrder, dispatch, hasUserReordered, service]);

  const handleRevert = React.useCallback(() => {
    dispatch(revertOrder() as any);
  }, [dispatch]);

  const handleManageCoders = React.useCallback(
    (queue: QueueItem) => {
      dispatch(openModal({ queueId: queue.id, queueName: queue.queueName }) as any);
    },
    [dispatch]
  );

  const handleTogglePin = React.useCallback(
    (queueId: string) => {
      dispatch(togglePin({ queueId }) as any);
    },
    [dispatch]
  );

  if (isLoading) {
    return (
      <Flex
        justify="center"
        align="center"
        minH="200px"
        data-testid="loading-spinner"
      >
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4} data-testid="error-state">
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  const buildListItems = (
    sectionQueues: QueueItem[],
    section: ReturnType<typeof useDragSection>,
    isPinnedSection: boolean
  ): React.ReactNode[] => {
    const draggableItems = sectionQueues.map(q => ({
      id: q.id,
      draggable: true,
      data: { id: q.id }
    }));

    const items: React.ReactNode[] = [];
    sectionQueues.forEach((queue, index) => {
      if (section.dragPreview?.domInsertIndex === index) {
        items.push(
          <PlaceholderCard
            key="drag-placeholder"
            onDrop={section.handlePlaceholderDrop}
          />
        );
      }
      items.push(
        <DraggableQueueCard
          key={queue.id}
          queue={queue}
          index={index}
          priority={section.dragPreview?.priorityMap.get(queue.id) ?? index + 1}
          item={draggableItems[index]}
          allItems={draggableItems}
          dm={section.dm}
          onDropHandler={section.handleDrop}
          onDragOverCallback={section.handleDragOverPreview}
          onDragEndCallback={section.clearPreview}
          onManageCoders={() => handleManageCoders(queue)}
          isPinned={isPinnedSection}
          onTogglePin={() => handleTogglePin(queue.id)}
          canTogglePin={isCodingManager}
        />
      );
    });
    if (section.dragPreview?.domInsertIndex === sectionQueues.length) {
      items.push(
        <PlaceholderCard key="drag-placeholder" onDrop={section.handlePlaceholderDrop} />
      );
    }
    return items;
  };

  const pinnedListItems = buildListItems(pinnedQueues, pinnedSection, true);
  const listItems = buildListItems(orderedQueues, mainSection, false);

  const actionsContent = (
    <Flex gap={6} align="center">
      {saveError && (
        <Text color="red.500" fontSize="sm" data-testid="save-error">
          {saveError}
        </Text>
      )}
      <Button
        onClick={handleRevert}
        isDisabled={!hasChanges || isSaving}
        variant="outline"
        data-testid="revert-priority-order-btn"
      >
        Revert
      </Button>
      <Button
        onClick={handleSave}
        isDisabled={!hasChanges || isSaving}
        isLoading={isSaving}
        colorScheme="blue"
        px={6}
        data-testid="save-priority-order-btn"
      >
        Save Priority Order
      </Button>
    </Flex>
  );

  return (
    <Box
      className="cmx-queue-priority-order-view"
      data-testid="queue-priority-order-view"
    >
      {headerActionsNode && createPortal(actionsContent, headerActionsNode)}
      <Flex
        className="cmx-queue-priority-order-view__header"
        justify="space-between"
        align="center"
        mb={8}
      >
        <Box>
          <Heading size="md">Queue Priority Order</Heading>
          <Text
            className="cmx-queue-priority-order-view__subtitle"
            fontSize="sm"
            mt={1}
          >
            Drag to reorder. Cases from higher-priority queues will be
            assigned to coders first. Use the{' '}
            <Text as="span" fontWeight="bold">
              Pin
            </Text>{' '}
            option to keep important queues at the top of the priority
            order. Pinned queues take precedence over all unpinned queues
            and are excluded from the system&apos;s nightly
            re-prioritization.
          </Text>
          {lastAutoResetLabel && (
            <Text
              className="cmx-queue-priority-order-view__last-reset"
              fontSize="sm"
              mt={1}
              data-testid="last-auto-reset"
            >
              {lastAutoResetLabel}
            </Text>
          )}
        </Box>
        {/* Falls back to rendering inline (e.g. when mounted outside the
            modal, in tests) if no header portal target is available. */}
        {!headerActionsNode && actionsContent}
      </Flex>

      {pinnedQueues.length > 0 && (
        <Box
          className="cmx-queue-priority-order-view__pinned-section"
          mb={20}
          data-testid="pinned-queue-list"
        >
          <Heading size="sm">
            Pinned Queues
          </Heading>
          <Text
            className="cmx-queue-priority-order-view__subtitle"
            fontSize="sm"
            mt={1}
            mb={5}
          >
            Not subject to nightly re-prioritization, to include a pinned
            queue in re-prioritization, unpin the queue and drag to desired
            priority position.
          </Text>
          <Box className="cmx-queue-priority-order-view__group">
            <Box
              ref={pinnedSection.rootRef}
              className="cmx-queue-priority-order-view__list"
            >
              {pinnedListItems}
            </Box>
          </Box>
        </Box>
      )}

      <Box className="cmx-queue-priority-order-view__group">
        <Box
          ref={mainSection.rootRef}
          className="cmx-queue-priority-order-view__list"
          data-testid="queue-list"
        >
          {orderedQueues.length === 0 && pinnedQueues.length === 0 ? (
            <Text data-testid="empty-state">No queues available.</Text>
          ) : (
            listItems
          )}
        </Box>
      </Box>
      <ManageCodersModalContainer />
    </Box>
  );
};

QueuePriorityOrderView.displayName = 'QueuePriorityOrderView';
export default QueuePriorityOrderView;
